import absurd from 'common/absurd';
import * as React from 'react';
import {selectAllFocusable} from 'src/selectAllFocusable';

export type FocusControls = {
  focusForward: () => void;
  focusBackward: () => void;
};
type Props = {
  behavior: 'trap' | 'continue';
  children: React.ReactNode;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
  onFocusOutside?: () => void;
};

const findNextFocusable = (
  root: HTMLElement | null,
  element: HTMLElement | null,
  offset: number,
) => {
  const allFocusable = selectAllFocusable(root);
  const foundIndex = allFocusable.findIndex((el) => el === element);
  const found = allFocusable[foundIndex + offset];
  return found;
};
const FocusTrap = (props: Props) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const startRef = React.useRef<HTMLDivElement | null>(null);
  const endRef = React.useRef<HTMLDivElement | null>(null);
  const originalFocus = React.useRef<HTMLElement | null>(null);
  const activeElement = document.activeElement;
  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    originalFocus.current = activeElement as HTMLElement;
  }, []);

  const focusBackward = () => {
    if (props.behavior === 'trap') {
      const next = findNextFocusable(containerRef.current, endRef.current, -1);
      next?.focus();
    } else if (props.behavior === 'continue') {
      const next = findNextFocusable(document.body, originalFocus.current, -1);
      next?.focus();
      props.onFocusOutside && props.onFocusOutside();
    } else {
      absurd(props.behavior);
    }
  };

  const focusForward = () => {
    if (props.behavior === 'trap') {
      const next = findNextFocusable(containerRef.current, startRef.current, 1);
      next?.focus();
    } else if (props.behavior === 'continue') {
      const next = findNextFocusable(document.body, originalFocus.current, 1);
      next?.focus();
      props.onFocusOutside && props.onFocusOutside();
    } else {
      absurd(props.behavior);
    }
  };

  if (props.focusControlsRef) {
    props.focusControlsRef.current = {focusForward, focusBackward};
  }

  const handleFocusStart = (e: React.FocusEvent) => {
    e.preventDefault();
    focusBackward();
  };
  const handleFocusEnd = (e: React.FocusEvent) => {
    e.preventDefault();
    focusForward();
  };

  return (
    <div ref={containerRef} className="contents">
      <div ref={startRef} tabIndex={0} onFocus={handleFocusStart} />
      {props.children}
      <div ref={endRef} tabIndex={0} onFocus={handleFocusEnd} />
    </div>
  );
};

export default FocusTrap;
